import { getEncryptParams } from 'src/keycloak';
import { DataApiInputParams } from 'src/types/api';
import {
  Builder,
  appendFormData,
  decryptResponse,
  getHeaders,
  getQueryString
} from './utils';
import { IS_ENCRYPTION_ENABLED } from 'src/config';

export default (builder: Builder) => {
  return {
    getAnalysisHistory: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-analysis?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['DocAnalyst']
    }),
    getAnalysis: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-analysis-detail?${getQueryString(
          arg.params
        )}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      }
    }),
    createAnalysis: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        let formData = appendFormData(arg.params);
        let url = `mini-experts/da-analysis`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    deleteAnalysis: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `mini-experts/da-analysis`;
        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    getDatasets: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-dataset?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['DocAnalyst']
    }),
    getDataset: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-dataset-details?${getQueryString(
          arg.params
        )}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['DocAnalyst']
    }),
    deleteDataset: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `mini-experts/da-dataset`;
        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    getQuestionnaires: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-questionnaire?${getQueryString(
          arg.params
        )}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },

      providesTags: ['DocAnalyst']
    }),
    getQuestionnaire: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-questionnaire-detail?${getQueryString(
          arg.params
        )}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      }
    }),
    createQuestionnaire: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        let formData = appendFormData(arg.params);
        let url = `mini-experts/da-questionnaire`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    editQuestionnaire: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        let formData = appendFormData(arg.params);

        let url = `mini-experts/da-questionnaire-detail`;
        return fetchWithBQ({
          url: url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    deleteQuestionnaire: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `mini-experts/da-questionnaire`;
        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    createDataset: builder.mutation({
      async queryFn({ params }, { getState }, extraOptions, fetchWithBQ) {
        let formData = appendFormData(params);
        return fetchWithBQ({
          url: `mini-experts/da-dataset`,
          method: 'POST',
          body: formData,
          ...getHeaders(`mini-experts/da-dataset`, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    getAnalysisDetail: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-analysis-detail?${getQueryString(
          arg.params
        )}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['DocAnalyst']
    }),
    updateAnalysisDetail: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        let formData = appendFormData(arg.params);
        let url = `mini-experts/da-analysis-detail`;
        return fetchWithBQ({
          url: url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      // TODO: remove invalidation and use the api response
      invalidatesTags: ['DocAnalyst']
    }),
    updateAnalysis: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        let formData = appendFormData(arg.params);
        return fetchWithBQ({
          url: `mini-experts/da-analysis`,
          method: 'PATCH',
          body: formData,
          ...getHeaders(`mini-experts/da-analysis`, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    }),
    getQuestionnaireSharedUsers: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `mini-experts/da-share-questionnaire?${getQueryString(
          arg.params
        )}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['DocAnalyst']
    }),
    sendQuestionnaire: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        let formData = appendFormData(arg.params);
        let url = `mini-experts/da-share-questionnaire`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['DocAnalyst']
    })
  };
};
